import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  TextField,
  Button,
  Typography,
  CircularProgress,
  Container,
  Paper,
  Box,
} from "@mui/material";
import { saveChapterEditorial, getChapterContentEditorial } from "../utils/api";

const ChapterEditorPageEditorial = () => {
  const { storyID, chapterIndex } = useParams();
  const navigate = useNavigate();

  const [chapter, setChapter] = useState({ title: "", content: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadChapterContent = async () => {
      try {
        // Fetch chapter content using API
        const { chapterName, content } = await getChapterContentEditorial({ storyID, chapterIndex });

        // Set the chapter content after fetching from the backend
        setChapter({ title: chapterName || `Chapter ${chapterIndex}`, content });
        setLoading(false);
      } catch (err) {
        console.error("Error loading chapter content:", err);
        setError("Failed to load chapter content.");
        setLoading(false);
      }
    };

    loadChapterContent();
  }, [storyID, chapterIndex]);

  const handleSaveChapter = async () => {
    setSaving(true);
    try {
      const contentBuffer = btoa(unescape(encodeURIComponent(chapter.content))); // Encode content as base64
      const paragraphCount = chapter.content.split("\n").filter((line) => line.trim() !== "").length;
  
      await saveChapterEditorial({
        storyID,
        chapterIndex: parseInt(chapterIndex),
        chapterName: chapter.title, // Use the chapter title as chapterName
        contentBuffer,
        paragraphCount, // Send the paragraph count
      });
  
      navigate(`/editorial/stories/${storyID}`); // Navigate to the editorial story details page
    } catch (err) {
      setError("Failed to save chapter.");
    } finally {
      setSaving(false);
    }
  };
  

  const handleChange = (e) => {
    setChapter({ ...chapter, [e.target.name]: e.target.value });
  };

  if (loading)
    return (
      <Container
        maxWidth="lg"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Container>
    );

  return (
    <Container maxWidth="lg" sx={{ marginY: 4 }}>
      <Paper
        sx={{
          padding: 4,
          borderRadius: 2,
          boxShadow: 3,
          position: "relative",
        }}
      >
        {/* Chapter Title */}
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            textAlign: "center",
            marginBottom: 4,
          }}
        >
          Edit Chapter {chapterIndex}
        </Typography>

        {error && (
          <Typography
            color="error"
            sx={{ textAlign: "center", marginBottom: 2 }}
          >
            {error}
          </Typography>
        )}

        {/* Chapter Title Input */}
        <TextField
          label="Chapter Title"
          variant="standard"
          fullWidth
          name="title"
          value={chapter.title}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
          sx={{ marginBottom: 4 }}
        />

        {/* Chapter Content Input */}
        <TextField
          label="Chapter Content"
          variant="standard"
          fullWidth
          multiline
          rows={14}
          name="content"
          value={chapter.content}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
          sx={{ marginBottom: 4 }}
        />

        {/* Save Button at the Bottom Right */}
        <Box
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            zIndex: 1,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveChapter}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{
              padding: 1,
              fontSize: 14,
              textTransform: "none",
            }}
          >
            {saving ? "Saving..." : "Save Chapter"}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default ChapterEditorPageEditorial;
